import type { CancelToken } from "axios";
import { defineStore } from "pinia";

import {
  DataStatus,
  type IIndivid,
  type IInfoStatus,
  type IKundserviceAppApiClient,
  type IMinaForetag,
  type IValidationProblemDetails,
  KundserviceAppApiClient,
  PlatformResponse_1OfIndivid,
  PlatformResponse_1OfList_1OfMinaForetag,
  ValidationProblemDetails,
} from "@/composables/KundserviceAppApiClient";
import { type INavigation, useNavigation } from "@/composables/useNavigation";

export type State = {
  individ: IndividState;
  minaForetag: MinaForetagState;
  kundkontaktkort: KundkontaktkortState;
  verification: VerifiedState;
};

type IndividState = {
  isLoading: boolean;
  data: IIndivid | null;
  infoStatus: Array<IInfoStatus>;
  error: IValidationProblemDetails | null;
};

type MinaForetagState = {
  isLoading: boolean;
  loaded: boolean;
  data?: IMinaForetag[] | null;
  infoStatus: Array<IInfoStatus>;
  bffError: IValidationProblemDetails | null;
  platformError: boolean;
};

type VerifiedState = {
  isAuthenticating: boolean;
  isVerified: boolean;
  isManuallyVerified: boolean;
  showManualVerification: boolean;
  personnummer: string | null;
};

type KundkontaktkortIndividUrl = {
  personnummer: string;
  url: string;
};

type KundkontaktkortArbetsgivareUrl = {
  organisationsnummer: string;
  url: string;
};

type KundkontaktkortState = {
  individer: KundkontaktkortIndividUrl[];
  arbetsgivare: KundkontaktkortArbetsgivareUrl[];
  isCreating: boolean;
};

const checkPlatformError = (status: DataStatus | null | undefined) => {
  return status === DataStatus.Error || status === DataStatus.Timeout;
};

export const useIndividStore = defineStore("individ", {
  state: (): State => {
    return {
      individ: {
        isLoading: false,
        data: null,
        infoStatus: [],
        error: null,
      },
      minaForetag: {
        isLoading: false,
        loaded: false,
        data: null,
        infoStatus: [],
        bffError: null,
        platformError: false,
      },
      kundkontaktkort: {
        individer: [],
        arbetsgivare: [],
        isCreating: false,
      },
      verification: {
        isAuthenticating: false,
        isVerified: false,
        isManuallyVerified: false,
        showManualVerification: false,
        personnummer: null,
      },
    };
  },
  getters: {
    shouldFetchAnstalldData: (state: State) => {
      return !!state.individ.data?.id &&
        !state.individ.data?.isAvliden &&
        !state.individ.data?.isAvtalatAnstalld;
    },
    canRenderIfIndividIsArbetsgivarerepresentant: (state: State) => {
      return (
        !state.verification.isAuthenticating && !state.minaForetag.isLoading
      );
    },
    getKundkontaktkortUrl:
      (state: State) =>
        (organisationsnummer?: string, personnummer?: string) => {
          if (organisationsnummer) {
            return state.kundkontaktkort.arbetsgivare.find(
              (x) => x.organisationsnummer === organisationsnummer,
            )?.url;
          }

          if (personnummer) {
            return state.kundkontaktkort.individer.find((x) => {
              return x.personnummer === personnummer;
            })?.url;
          }
        },
    isArbetsgivarerepresentant:
      (state: State) =>
        (currentOrganisationsnummer: string | null | undefined) => {
          return (
            currentOrganisationsnummer &&
            state.minaForetag.data?.some(
              (x) =>
                x.organisationsnummer ===
                currentOrganisationsnummer.replace("-", ""),
            )
          );
        },
  },
  actions: {
    onBeforeVerification(personnummer: string) {
      this.$patch((store) => {
        store.verification.isAuthenticating = true;
        store.verification.personnummer = personnummer;
      });
    },
    onVerificationSuccess() {
      this.$patch((store) => {
        store.verification.isVerified = true;
        store.verification.isAuthenticating = false;
      });
    },
    async fetchMinaForetag(
      individId: string,
      apiClient: KundserviceAppApiClient,
      cancelToken: CancelToken | undefined = undefined,
    ) {
      try {
        this.minaForetag.isLoading = true;
        this.minaForetag.loaded = false;
        this.$patch((store) => {
          store.minaForetag.isLoading = true;
        });
        const minaForetagResponse = await apiClient.getMinaForetag(
          individId,
          cancelToken,
        );
        this.minaForetag.loaded = true;

        if (minaForetagResponse?.data != null) {
          this.onGetMinaForetagSuccess(minaForetagResponse);
        }
      } catch (error) {
        this.onGetMinaForetagError(
          error as PlatformResponse_1OfList_1OfMinaForetag,
          individId,
        );
      }
    },
    onGetMinaForetagSuccess(
      minaforetagResponse: PlatformResponse_1OfList_1OfMinaForetag,
    ) {
      this.$patch((store) => {
        store.minaForetag.isLoading = false;
        store.minaForetag.data = minaforetagResponse.data;
        store.minaForetag.infoStatus = store.minaForetag.infoStatus =
          minaforetagResponse.statuses
            ? minaforetagResponse.statuses
            : ([] as IInfoStatus[]);

        const hasPlatformError = store.minaForetag.infoStatus.some((x) =>
          checkPlatformError(x.status),
        );
        if (hasPlatformError) {
          store.minaForetag.platformError = true;
        }
      });
    },
    onGetMinaForetagError(
      error: PlatformResponse_1OfList_1OfMinaForetag,
      personnummer: string | undefined,
    ) {
      if (error instanceof ValidationProblemDetails) {
        const navigation = useNavigation();
        navigation.notFound(personnummer);

        return;
      }

      this.$patch((store) => {
        this.minaForetag.isLoading = false;
        store.minaForetag.bffError = error || "Error.";
      });
    },
    async reloadIndivid(apiClient: IKundserviceAppApiClient) {
      try {
        const individResponse = await apiClient.getIndivid(
          this.$state.individ.data?.id,
          undefined,
        );
        if (individResponse?.data) {
          this.$state.individ.data = individResponse.data as IIndivid;
        }
      } catch (error) {
        console.error(error);
      }
    },
    async fetchIndivid(
      individId: string | undefined,
      personnummer: string | undefined,
      apiClient: IKundserviceAppApiClient,
    ) {
      try {
        const navigation = useNavigation();
        this.$patch((store) => {
          store.individ.isLoading = true;
          store.individ.error = null;
        });

        const individResponse = await apiClient.getIndivid(
          individId,
          personnummer,
        );
        await this.onGetIndividSuccess(
          individResponse,
          personnummer,
          navigation,
        );
      } catch (error) {
        this.onGetIndividError(
          error as PlatformResponse_1OfIndivid,
          personnummer,
        );
      }
    },
    async onGetIndividSuccess(
      individResponse: PlatformResponse_1OfIndivid,
      personnummer: string | undefined,
      navigation: INavigation,
    ) {
      if (!individResponse.data?.id) {
        this.$patch((store) => {
          store.individ.isLoading = false;
          store.individ.data = null;
        });

        navigation.notFound(personnummer);
        return;
      }

      await navigation.replacePersonnummerWithId(individResponse.data.id);

      const isVerified =
        navigation.router.currentRoute.value.query.ver
          ?.toString()
          .toLowerCase() === "true";
      const isManuallyVerified =
        navigation.router.currentRoute.value.query.manver
          ?.toString()
          .toLowerCase() === "true";

      this.$patch((store) => {
        store.verification.isVerified = isVerified;
        store.verification.isManuallyVerified = isManuallyVerified;
        store.individ.isLoading = false;
        store.individ.data = individResponse.data as IIndivid;
        store.individ.infoStatus = individResponse.statuses
          ? individResponse.statuses
          : ([] as IInfoStatus[]);
      });

      navigation.clearVerified();
    },
    onGetIndividError(
      error: PlatformResponse_1OfIndivid,
      personnummer: string | undefined,
    ) {
      if (error instanceof ValidationProblemDetails) {
        const navigation = useNavigation();
        navigation.notFound(personnummer);
        return;
      }
      console.error(error);
      this.$patch((store) => {
        store.individ.isLoading = false;
        store.individ.error = error || "Error.";
      });
    },
    onKundkontaktkortCreating() {
      this.kundkontaktkort.isCreating = true;
    },
    onKundkontaktkortCreated(
      url: string,
      personnummer?: string,
      organisationsnummer?: string,
    ) {
      this.$patch((store) => {
        store.kundkontaktkort.isCreating = false;

        if (organisationsnummer) {
          store.kundkontaktkort.arbetsgivare.push({ organisationsnummer, url });
        } else if (personnummer) {
          store.kundkontaktkort.individer.push({ personnummer, url });
        }
      });
    },
  },
});
